import { useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { cleanSocials, parseRating, parseCount } from '../../lib/tenantSettings';
import { SOCIAL_NETWORKS, type SocialKey } from '../../lib/socials';
import { saveTenantInfo, saveTenantExtra, fetchGoogleRating } from '../../lib/tenantSettingsService';
import { uploadProductImage } from '../../lib/storage';

/**
 * Section « Mon restaurant » de l'admin (parité legacy Brand tab) :
 * identité visuelle (logo, couleurs, aperçu hero), informations (adresse,
 * téléphone, email), notes Google/Uber, réseaux sociaux. Les horaires ont
 * leur propre section. Enregistré via saveTenantInfo (update direct, RLS admin).
 */
export default function TenantSettings({ tenant }: { tenant: Tenant; defaultOpen?: boolean }) {
  const [name, setName] = useState(tenant.name ?? '');
  const [city, setCity] = useState(tenant.city ?? '');
  const [manager, setManager] = useState(tenant.manager_name ?? '');
  const [socials, setSocials] = useState<Record<SocialKey, string>>(() => {
    const s = {} as Record<SocialKey, string>;
    SOCIAL_NETWORKS.forEach((n) => (s[n.key] = (tenant.socials?.[n.key] as string) ?? ''));
    return s;
  });
  const [ratingGoogle, setRatingGoogle] = useState(tenant.rating_google ? String(tenant.rating_google) : '');
  const [ratingGoogleCount, setRatingGoogleCount] = useState(tenant.rating_google_count ? String(tenant.rating_google_count) : '');
  const [ratingUber, setRatingUber] = useState(tenant.rating_uber ? String(tenant.rating_uber) : '');
  const [ratingUberCount, setRatingUberCount] = useState(tenant.rating_uber_count ? String(tenant.rating_uber_count) : '');

  const [tagline, setTagline] = useState(tenant.tagline ?? '');
  const [shortPitch, setShortPitch] = useState(tenant.short_pitch ?? '');
  const [heroImageUrl, setHeroImageUrl] = useState(tenant.hero_image_url ?? '');
  const [logoUrl, setLogoUrl] = useState(tenant.logo_url ?? '');
  const [primaryColor, setPrimaryColor] = useState(tenant.primary_color ?? '#E65100');
  const [secondaryColor, setSecondaryColor] = useState(tenant.secondary_color ?? '#2C1810');
  const [addressLine, setAddressLine] = useState(tenant.address_line ?? '');
  const [postalCode, setPostalCode] = useState(tenant.postal_code ?? '');
  const [phone1, setPhone1] = useState(tenant.phone1 ?? '');
  const [email, setEmail] = useState(tenant.email ?? '');
  const [legalName, setLegalName] = useState(tenant.legal_name ?? '');
  const [siret, setSiret] = useState(tenant.siret ?? '');
  const [googleReviewUrl, setGoogleReviewUrl] = useState(tenant.google_review_url ?? '');
  const [uberUrl, setUberUrl] = useState((tenant.socials?.uber as string) ?? '');

  const [busy, setBusy] = useState(false);
  const [busyGoogle, setBusyGoogle] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [upLogo, setUpLogo] = useState(false);
  const [upHero, setUpHero] = useState(false);
  const shopUrl = `/r/${tenant.slug}`;

  /** Téléverse un fichier image vers le Storage et place l'URL publique dans le champ. */
  const onUpload = async (
    file: File | undefined,
    setUrl: (u: string) => void,
    setUploading: (b: boolean) => void,
  ) => {
    if (!file) return;
    setUploading(true);
    setMsg(null);
    const url = await uploadProductImage(tenant.id, file);
    setUploading(false);
    if (url) {
      setUrl(url);
      setMsg({ tone: 'ok', text: 'Image téléversée — pensez à Enregistrer.' });
    } else {
      setMsg({ tone: 'err', text: 'Téléversement impossible (le bucket « product-images » doit exister et être public).' });
    }
  };

  /** Récupère la note Google (API Places) d'après le nom + l'adresse et remplit les champs. */
  const refreshGoogleRating = async () => {
    const query = [name, addressLine, postalCode, city].map((s) => s.trim()).filter(Boolean).join(' ');
    if (query.length < 3) {
      setMsg({ tone: 'err', text: 'Renseignez d’abord le nom et l’adresse du restaurant.' });
      return;
    }
    setBusyGoogle(true);
    setMsg(null);
    const r = await fetchGoogleRating(query);
    setBusyGoogle(false);
    if (r.ok && r.rating != null) {
      setRatingGoogle(String(r.rating));
      setRatingGoogleCount(String(r.count ?? 0));
      setMsg({
        tone: 'ok',
        text: `Note Google récupérée : ${r.rating}/5 · ${r.count ?? 0} avis${r.name ? ` (${r.name})` : ''} — pensez à Enregistrer.`,
      });
    } else {
      setMsg({ tone: 'err', text: r.error || 'Note Google introuvable.' });
    }
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    const r = await saveTenantInfo({
      tenantId: tenant.id,
      name: name.trim() || undefined, // undefined si vide → le nom n'est jamais effacé
      city: city.trim() || null,
      managerName: manager.trim() || null,
      // Réseaux affichés + lien Uber (interne, non affiché sur le site) dans le même jsonb.
      socials: { ...cleanSocials(socials), ...(uberUrl.trim() ? { uber: uberUrl.trim() } : {}) },
      ratingGoogle: parseRating(ratingGoogle),
      ratingGoogleCount: parseCount(ratingGoogleCount),
      ratingUber: parseRating(ratingUber),
      ratingUberCount: parseCount(ratingUberCount),
      tagline: tagline.trim() || null,
      shortPitch: shortPitch.trim() || null,
      heroImageUrl: heroImageUrl.trim() || null,
      logoUrl: logoUrl.trim() || null,
      primaryColor: primaryColor.trim() || null,
      secondaryColor: secondaryColor.trim() || null,
      addressLine: addressLine.trim() || null,
      postalCode: postalCode.trim() || null,
      phone1: phone1.trim() || null,
      email: email.trim() || null,
    });
    // Champs extra (résilient : ne casse pas si la migration n'est pas lancée).
    await saveTenantExtra({
      tenantId: tenant.id,
      legalName: legalName.trim() || null,
      siret: siret.trim() || null,
      googleReviewUrl: googleReviewUrl.trim() || null,
    });
    setBusy(false);
    setMsg(r.ok ? { tone: 'ok', text: 'Enregistré.' } : { tone: 'err', text: r.error || 'Erreur.' });
  };

  const field = 'rounded-lg border border-slate-200 px-3 py-2';

  return (
    <div className="flex flex-col gap-5">
      {/* Identité visuelle */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-base font-bold">🎨 Identité visuelle</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Logo</span>
            <div className="flex items-center gap-2">
              {logoUrl && <img src={logoUrl} alt="" className="h-12 w-12 rounded-lg object-contain border border-slate-100" />}
              <label className="cursor-pointer rounded-lg border border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-light">
                {upLogo ? 'Téléversement…' : '📤 Choisir un fichier'}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={upLogo} onChange={(e) => onUpload(e.target.files?.[0], setLogoUrl, setUpLogo)} />
              </label>
            </div>
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="…ou collez une URL (https://…/logo.png)" className={field + ' mt-1 text-xs'} />
          </div>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Couleur principale</span>
            <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200" />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Couleur foncée (dégradé)</span>
            <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200" />
          </label>
          <div className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Photo de fond (hero)</span>
            <div className="flex items-center gap-2">
              {heroImageUrl && <img src={heroImageUrl} alt="" className="h-12 w-20 rounded-lg object-cover border border-slate-100" />}
              <label className="cursor-pointer rounded-lg border border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-light">
                {upHero ? 'Téléversement…' : '📤 Choisir un fichier'}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={upHero} onChange={(e) => onUpload(e.target.files?.[0], setHeroImageUrl, setUpHero)} />
              </label>
            </div>
            <input value={heroImageUrl} onChange={(e) => setHeroImageUrl(e.target.value)} placeholder="…ou collez une URL (https://…/photo.jpg)" className={field + ' mt-1 text-xs'} />
          </div>
        </div>
        {/* Aperçu hero */}
        <div className="mt-3 flex h-24 items-center justify-center rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
          <span className="text-sm font-semibold opacity-90">Aperçu hero du site</span>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Titre d'accroche (hero)</span>
            <input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Commandez chez … en retrait ou en livraison." className={field} />
          </label>
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Phrase de présentation</span>
            <input value={shortPitch} onChange={(e) => setShortPitch(e.target.value)} placeholder="Vos spécialités en une phrase…" className={field} />
          </label>
        </div>
      </div>

      {/* Informations restaurant */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-base font-bold">📍 Informations restaurant</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Nom du restaurant</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Le Soleil du Maghreb" className={field} />
            <span className="mt-0.5 text-[11px] text-slate-400">C'est le nom affiché en haut de l'admin et sur votre site.</span>
          </label>
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Adresse</span>
            <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="12 rue de …" className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Code postal</span>
            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Ville</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Téléphone</span>
            <input value={phone1} onChange={(e) => setPhone1(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Raison sociale</span>
            <input value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Ex. : BIG BURGER SARL" className={field} />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">SIRET</span>
            <input value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="123 456 789 00012" className={field} />
          </label>
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Gérant (greeting admin « Bonjour, … »)</span>
            <input value={manager} onChange={(e) => setManager(e.target.value)} className={field} />
          </label>
        </div>
      </div>

      {/* Liens Google Business & Uber Eats */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 text-base font-bold">⭐ Liens Google Business & Uber Eats</h3>
        <p className="mb-3 text-xs text-slate-500">
          Vos pages de référence pour les avis. Le lien Google sert aux demandes d'avis (clients satisfaits, NPS 9-10) ;
          le lien Uber Eats sert au suivi de vos notes/avis (usage interne, jamais affiché sur votre site).
        </p>
        <label className="mb-2 flex flex-col">
          <span className="text-xs font-semibold opacity-60">Lien avis Google (Google Business Profile)</span>
          <input
            value={googleReviewUrl}
            onChange={(e) => setGoogleReviewUrl(e.target.value)}
            placeholder="https://g.page/r/.../review"
            className={field + ' w-full'}
          />
        </label>
        <label className="flex flex-col">
          <span className="text-xs font-semibold opacity-60">Lien de votre page Uber Eats</span>
          <input
            value={uberUrl}
            onChange={(e) => setUberUrl(e.target.value)}
            placeholder="https://www.ubereats.com/fr/store/..."
            className={field + ' w-full'}
          />
        </label>
        <p className="mt-2 rounded-lg bg-sky-50 p-2 text-xs text-sky-800">
          💡 Google : connectez-vous à Google Business Profile → Partager → « Demander des avis » → copiez le lien court.
          <span className="opacity-70"> La note Google se remplit automatiquement via le bouton « Récupérer ma note Google » (section Notes).</span>
        </p>
      </div>

      {/* Notes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-base font-bold">⭐ Notes (vide = masquée)</h3>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={refreshGoogleRating}
            disabled={busyGoogle}
            className="rounded-lg border border-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary hover:bg-brand-light disabled:opacity-50"
          >
            {busyGoogle ? 'Recherche…' : '🔄 Récupérer ma note Google'}
          </button>
          <span className="text-[11px] text-slate-400">
            Automatique, d'après le nom + l'adresse ci-dessus. (Uber reste manuel : pas d'API.)
          </span>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Note Google (0–5)</span>
            <input value={ratingGoogle} onChange={(e) => setRatingGoogle(e.target.value)} placeholder="4.7" className="w-28 rounded border border-slate-200 px-2 py-1" />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Avis Google</span>
            <input value={ratingGoogleCount} onChange={(e) => setRatingGoogleCount(e.target.value)} placeholder="217" className="w-24 rounded border border-slate-200 px-2 py-1" />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Note Uber (0–5)</span>
            <input value={ratingUber} onChange={(e) => setRatingUber(e.target.value)} className="w-28 rounded border border-slate-200 px-2 py-1" />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Avis Uber</span>
            <input value={ratingUberCount} onChange={(e) => setRatingUberCount(e.target.value)} className="w-24 rounded border border-slate-200 px-2 py-1" />
          </label>
        </div>
      </div>

      {/* Réseaux sociaux */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-base font-bold">🔗 Réseaux sociaux (vides = masqués)</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {SOCIAL_NETWORKS.map((n) => (
            <label key={n.key} className="flex flex-col text-sm">
              <span className="text-xs font-semibold opacity-60">{n.label}</span>
              <input value={socials[n.key]} onChange={(e) => setSocials((cur) => ({ ...cur, [n.key]: e.target.value }))} placeholder="https://…" className="rounded border border-slate-200 px-2 py-1" />
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="rounded-xl px-5 py-2.5 font-semibold text-white disabled:opacity-40" style={{ background: 'var(--brand-primary)' }}>
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50">
          Voir ma boutique ↗
        </a>
        {msg && <span className={`text-sm ${msg.tone === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{msg.text}</span>}
      </div>
    </div>
  );
}
